import React, { ReactElement, useMemo } from 'react'
import { PrimaryButton } from '../../components/Button'
import { InputLabel, OptionalLabel, TextInputWithLabel } from '../../components/Input'
import PageHeader from '../../components/PageHeader'
import { AccountServicesType, LegalAndTaxDetails as State } from '../../state/Radom'
import { BusinessTaxNumberTypes } from './TaxNumberTypes'
import SearchMultiselect from '../../components/SearchMultiselect'
import Tooltip from '../../components/Tooltip'
import { highlightInvalidFields } from '../../util/Util'
import { CryptoAccountActivity, FiatAccountActivity, SourceOfFunds } from './Util'
import { snakeCase, startCase } from 'lodash'
import { Countries } from './Countries'
import { RADOM_COLORS } from '../../util/Constants'
import PDFFileUpload from './PDFFileUpload'
import { VerificationRequirementError } from '../../components/VerificationRequirementError'

type LegalAndTaxDetailsStateExtended = State & {
  proofOfWealthFile?: File
  proofOfWealthUrl?: string
}
interface IProps {
  state: LegalAndTaxDetailsStateExtended
  countryCode: string
  isSubmitting: boolean
  accountType: AccountServicesType
  onUpdate: (value: any) => void
  onSubmit: () => void
  getError: (fieldId: string) => string | undefined
}

const LegalAndTaxDetails = ({ state, countryCode, isSubmitting, onUpdate, onSubmit, accountType, getError }: IProps):
ReactElement => {
  const canSubmit = useMemo(() => {
    const baseValidation = !!(state.expectedCryptoActivity && state.expectedOriginsOfFunds)

    if (accountType === AccountServicesType.crypto_and_fiat) {
      return (
        baseValidation &&
      !!state.expectedFiatActivity &&
      !!(state.proofOfWealthFile || state.proofOfWealth) &&
      !!state.sourceOfFunds &&
      !!state.taxIdentificationNumberType &&
      (state.taxIdentificationNumber || '').trim() !== ''
      )
    }

    return baseValidation
  }, [state, accountType])

  const Continents = [
    { name: 'Europe', alpha3: 'EUROPE' },
    { name: 'Asia', alpha3: 'ASIA' },
    { name: 'Africa', alpha3: 'AFRICA' },
    { name: 'North America', alpha3: 'NORTH_AMERICA' },
    { name: 'South America', alpha3: 'SOUTH_AMERICA' },
    { name: 'Oceania', alpha3: 'OCEANIA' },
    { name: 'Antarctica', alpha3: 'ANTARCTICA' }
  ]

  const originsOfFundsOptions = useMemo(() => {
    return [
      ...Continents.map(c => ({ key: c.name, value: c.alpha3 })),
      ...Countries.map(s => ({ key: s.name, value: s.alpha3 }))
    ]
  }, [])

  const selectedOriginsOfFunds = useMemo(() => {
    if (!state.expectedOriginsOfFunds) return []
    return state.expectedOriginsOfFunds.split(',').map(code => {
      const trimmedCode = code.trim()
      const continent = Continents.find(c => c.alpha3 === trimmedCode)
      const country = Countries.find(c => c.alpha3 === trimmedCode)
      return {
        key: continent?.name ?? country?.name ?? trimmedCode,
        value: trimmedCode
      }
    })
  }, [state.expectedOriginsOfFunds])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontSize: 14, paddingBottom: 40 }}>
      <div style={{ marginBottom: 20 }}>
        <PageHeader title="Share your legal details" subTitle="Provide your legal and tax information" />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <InputLabel>Expected crypto account activity</InputLabel>
          <Tooltip message='Expected use of crypto services?' />
        </div>
        <VerificationRequirementError message={getError('expectedCryptoActivity')} />

        <SearchMultiselect
          isSingleSelect
          placeholder="Expected use of crypto services"
          dropdownPlaceholder="No expected crypto account activity found"
          itemArray={CryptoAccountActivity.map(a => ({ key: snakeCase(a), value: startCase(a) })) ?? []}
          selectedItems={state.expectedCryptoActivity
            ? [{
              key: state.expectedCryptoActivity,
              value: startCase(state.expectedCryptoActivity)
            }]
            : []}
          setSelectedItems={(items) => {
            const selected = items[0]
            if (selected) {
              onUpdate({ ...state, expectedCryptoActivity: snakeCase(selected.value) })
            }
          }}
          keyExtractor={(country) => country.key}
          labelExtractor={(country) => country.value}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <InputLabel>Expected fiat account activity</InputLabel>
          <Tooltip message='Expected use of fiat services?' />
        </div>
        <VerificationRequirementError message={getError('expectedFiatActivity')} />

        <SearchMultiselect
          isSingleSelect
          placeholder="Expected use of fiat services"
          dropdownPlaceholder="No expected use of fiat services found"
          itemArray={FiatAccountActivity.map(a => ({ key: snakeCase(a), value: startCase(a) })) ?? []}
          selectedItems={state.expectedFiatActivity
            ? [{
              key: state.expectedFiatActivity,
              value: startCase(state.expectedFiatActivity)
            }]
            : []}
          setSelectedItems={(items) => {
            const selected = items[0]
            if (selected) {
              onUpdate({ ...state, expectedFiatActivity: snakeCase(selected.value) })
            }
          }}
          keyExtractor={(country) => country.key}
          labelExtractor={(country) => country.value}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <InputLabel>Expected country of origin for incoming funds</InputLabel>
          <Tooltip message='Expected country of origin for incoming funds?' />
        </div>
        <VerificationRequirementError message={getError('expectedOriginsOfFunds')} />

        <SearchMultiselect
          showMultiSelectIndicator={true}
          placeholder="Select countries or continents"
          dropdownPlaceholder="No countries found"
          itemArray={originsOfFundsOptions}
          selectedItems={selectedOriginsOfFunds}
          setSelectedItems={(items) => {
            const valuesString = items.map(item => item.value).join(',')
            onUpdate({ ...state, expectedOriginsOfFunds: valuesString })
          }}
          keyExtractor={(country) => country.value}
          labelExtractor={(country) => country.key}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <InputLabel>Source of funds/Source of wealth</InputLabel>
          <Tooltip message='Where do your funds originate?' />
        </div>
        <VerificationRequirementError message={getError('sourceOfFunds')} />

        <SearchMultiselect
          isSingleSelect
          placeholder="Select source of funds"
          dropdownPlaceholder="No source of funds found"
          itemArray={SourceOfFunds.map(a => ({ key: snakeCase(a), value: startCase(a) })) ?? []}
          selectedItems={state.sourceOfFunds
            ? [{
              key: state.sourceOfFunds,
              value: startCase(state.sourceOfFunds)
            }]
            : []}
          setSelectedItems={(items) => {
            const selected = items[0]
            if (selected) {
              onUpdate({ ...state, sourceOfFunds: snakeCase(selected.value) })
            }
          }}
          keyExtractor={(country) => country.key}
          labelExtractor={(country) => country.value}
        />
      </div>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10 }}>
          <InputLabel>
            Proof of funds/wealth
          </InputLabel>
          <OptionalLabel>PDF</OptionalLabel>
          <Tooltip message='Upload a document proving your operating address, such as a utility bill or bank statement.' />
        </div>
        {getError('proofOfWealth') && <div style={{ marginBottom: 10 }}><VerificationRequirementError message={getError('proofOfWealth')} /></div>}
        <div style={{ display: 'flex', gap: 10, flexDirection: 'column' }}>
          <div style={{
            border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
            padding: 20,
            background: RADOM_COLORS.GRAY9,
            borderRadius: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 10
          }}>
            <span>Upload one of the following issued to the applying entity:</span>
            <ul style={{ paddingLeft: 30, margin: 0 }}>
              <li><b>Financial predictions</b> </li>
              <li><b>Bank statement</b> (less than 90 days old)</li>
              <li><b>Crypto wallet holdings</b></li>
            </ul>
          </div>
          <PDFFileUpload
            file={state.proofOfWealthUrl ?? state.proofOfWealthFile}
            onFile={f => onUpdate({ ...state, proofOfWealthFile: f })}
            onRemove={() => {
              onUpdate({
                ...state,
                proofOfWealth: '',
                proofOfWealthUrl: undefined,
                proofOfWealthFile: undefined,
                lastUploadedProofOfWealthFileName: undefined
              })
            }} />
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <InputLabel>Tax identification number type</InputLabel>
          <Tooltip message='Select the type of tax ID for your jurisdiction' />
        </div>
        <VerificationRequirementError message={getError('taxIdentificationNumberType')} />

        <SearchMultiselect
          isSingleSelect
          placeholder="Select tax number type"
          dropdownPlaceholder="No tax number types found"
          itemArray={BusinessTaxNumberTypes[countryCode]?.map(a => ({ key: a.description, value: a.value })) ?? []}
          selectedItems={state.taxIdentificationNumberType
            ? [{
              key: state.taxIdentificationNumberType,
              value: state.taxIdentificationNumberType
            }]
            : []}
          setSelectedItems={(items) => {
            const selected = items[0]
            if (selected) {
              onUpdate({ ...state, taxIdentificationNumberType: selected.value })
            }
          }}
          keyExtractor={(country) => country.value}
          labelExtractor={(country) => country.key}
        />
      </div>

      <TextInputWithLabel
        required
        label="Tax identification number"
        tooltip="Your business tax ID number"
        placeholder="Enter your tax identification number"
        value={state.taxIdentificationNumber}
        errorMessage={getError('taxIdentificationNumber')}

        onChange={(event) => {
          const eventTarget = event.target as HTMLInputElement
          onUpdate({ ...state, taxIdentificationNumber: eventTarget.value })
        }}
      />

      <PrimaryButton
        disabled={isSubmitting}
        isLoading={isSubmitting}
        onClick={() => {
          if (canSubmit) {
            onSubmit()
          } else {
            highlightInvalidFields()
          }
        }}>
        Continue
      </PrimaryButton>
    </div>
  )
}

export default LegalAndTaxDetails
